import useGetMyAccountIntegrations from '@/api/queries/me/useGetMyAccountIntegrations';
import { useSelector } from '@/api/state';
import {
  ConnectionControl,
  ConnectionControlBenefitList,
} from '@/ui/components';
import { registerFieldLegacyBuilder } from '@/utils/zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Field, Modal, RawInput } from '@payaca/ambrosia';

import { AccountsPermissions } from '@payaca/permissions/accounts/accounts.permissions';
import { userHasRequiredPermission } from '@payaca/permissions/permissions.utils';
import { FC, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod/v4';
import {
  useConnectAccountToHeatpunk,
  useDisconnectAccountFromHeatpunk,
} from '../../../../api/mutations/integrations/useConnectAccountToHeatpunk';
import { getUserRoles } from '../../../../utils/stateAccessors';
import HeatpunkLogo from './heatpunk.svg';

const formSchema = z.object({
  apiKey: z.string().min(1),
});
type FormSchema = z.infer<typeof formSchema>;

export const HeatpunkConnectionControl: FC = () => {
  const [isSettingUpConnection, setIsSettingUpConnection] = useState(false);
  const disconnectAccountFromHeatpunk = useDisconnectAccountFromHeatpunk();
  const { data: integrations, isLoading: isLoadingIntegrations } =
    useGetMyAccountIntegrations();
  const hasConnection = integrations.some(
    (accountIntegration) => accountIntegration.service === 'HEATPUNK'
  );
  const isLoading = isLoadingIntegrations;
  const userRoles = useSelector(getUserRoles);

  const canUpdate = useMemo(() => {
    return userHasRequiredPermission(userRoles, [
      AccountsPermissions.UPDATE_DESIGN_INTEGRATION,
    ]);
  }, [userRoles]);

  return (
    <ConnectionControl
      allowConnectDisconnect={canUpdate && !isLoading}
      connectionName="Heatpunk"
      isConnected={hasConnection}
      onAddConnection={() => setIsSettingUpConnection(true)}
      onRemoveConnection={() => disconnectAccountFromHeatpunk.mutateAsync({})}
      logo={HeatpunkLogo}
    >
      {hasConnection ? (
        <p>Your Heatpunk account has successfully been connected to Payaca.</p>
      ) : (
        <>
          Connect Payaca with Heatpunk for easy syncing of your Projects.
          <ConnectionControlBenefitList
            benefits={[
              'Create a Heatpunk project from Payaca.',
              'Sync your files from Heatpunk into Payaca.',
            ]}
          />
        </>
      )}
      <Modal
        isOpen={isSettingUpConnection}
        onClose={() => setIsSettingUpConnection(false)}
        title="Connect your Heatpunk account"
      >
        <HeatpunkConnectionForm
          onSuccess={() => setIsSettingUpConnection(false)}
        />
      </Modal>
    </ConnectionControl>
  );
};

const HeatpunkConnectionForm: FC<{ onSuccess: () => void }> = ({
  onSuccess,
}) => {
  const { mutateAsync: connectAccountToHeatpunk } =
    useConnectAccountToHeatpunk();

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
    setError,
    clearErrors,
    control,
  } = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
  });

  const registerField = registerFieldLegacyBuilder(errors);
  const onSubmit = async (data: FormSchema) => {
    const response = await connectAccountToHeatpunk(data);
    switch (response.connectAccountToHeatpunk.__typename) {
      case 'ConnectAccountToHeatpunkSuccess': {
        return onSuccess();
      }
      case 'ConnectAccountToHeatpunkInvalidAPIKey': {
        return setError('root', {
          type: 'server',
          message:
            'The API key you entered appears to be invalid. Please check your Heatpunk account and try again.',
        });
      }
      case 'ConnectAccountToHeatpunkError': {
        return setError('root', {
          type: 'server',
          message: 'Sorry, we were unable to add your Heatpunk account.',
        });
      }
      default: {
        return setError('root', {
          type: 'server',
          message: 'An unknown error occurred, please try again later.',
        });
      }
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Modal.Body>
        <p className="mb-4">
          Please enter the API key that Heatpunk has provided for you. If you do
          not already have one,{' '}
          <a
            href="https://help.heatpunk.co.uk/books/developer-guidelines/page/getting-started"
            target="_blank"
            rel="noreferrer"
          >
            this guide
          </a>{' '}
          should help you get started.
        </p>
        <div className="space-y-2.5">
          <Field.Legacy {...registerField('apiKey')}>
            <Field.Label>API key</Field.Label>
            <RawInput
              {...register('apiKey')}
              placeholder="Your Heatpunk API key"
              autoComplete="off"
              onChange={(change) => {
                clearErrors('root');
                void register('apiKey').onChange(change);
              }}
            />
          </Field.Legacy>
          <Field.Legacy {...registerField('root')} />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Actions>
          <Button type="submit" isProcessing={isSubmitting}>
            Connect
          </Button>
        </Modal.Footer.Actions>
      </Modal.Footer>
    </form>
  );
};
